import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";

import { notify } from "../../../components/Notification";
import { ErrorState } from "../../../components/States";
import {
  useCreateSavingsMutation,
  useGetSavingsHistoryQuery,
  useGetSavingsQuery,
  useGetSavingsYearsQuery,
  SavingsHistoryResponse,
} from "../../../services";
import { formatDate } from "../../../utils";
import { cashbackPayload } from "../../../utils/cashback";
import { Wrapper } from "./styles";
import { SavingFormValues, SavingsPanel } from "../panels";
import { CreateSavingsPayload } from "../types";
import { headlineInterestOf } from "../utils";
import {
  EmptySavingsState,
  SavingsFilters,
  SavingsHero,
  SavingsHoldings,
  SavingsInsights,
  SavingsSkeleton,
} from "./components";
import { SortState } from "./components/SavingsHoldings";
import { CreateFab } from "../../../components/CreateFab";
import { useResponsive } from "../../../hooks/useResponsive";
import { BREAKPOINTS } from "../../../utils/constants";
import { buildSavingsGrowth } from "./savingsGrowth";
import { PeriodDataStatus } from "./components/types";

const EMPTY_HISTORY: SavingsHistoryResponse = {
  data: [],
  summary: {
    currentValue: 0,
    contributedValue: 0,
    earnedValue: 0,
    changePercent: null,
  },
  bySaving: [],
};

// Sentinel period value covering every year, surfaced as the first option.
const ALL_TIME = "all";

const periodStatusOf = (query: {
  currentData?: unknown;
  isFetching: boolean;
  isError: boolean;
}): PeriodDataStatus => {
  if (query.currentData !== undefined) {
    return "ready";
  }
  if (query.isFetching) {
    return "loading";
  }
  return query.isError ? "unavailable" : "ready";
};

export const SavingsPage = () => {
  const [, setLocation] = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const isCompact = useResponsive(BREAKPOINTS.tabletLarge);

  const [activeOnly, setActiveOnly] = useState(true);
  const [periodValue, setPeriodValue] = useState(ALL_TIME);
  const [sort, setSort] = useState<SortState>({ key: "balance", dir: "desc" });

  const selectedYear =
    periodValue === ALL_TIME ? undefined : Number(periodValue);

  const savingsQuery = useGetSavingsQuery({ activeOnly, year: selectedYear });
  const historyQuery = useGetSavingsHistoryQuery({
    activeOnly,
    year: selectedYear,
  });
  const yearsQuery = useGetSavingsYearsQuery(activeOnly);
  const allSavingsQuery = useGetSavingsQuery({ activeOnly: false });
  const [createSavings] = useCreateSavingsMutation();

  const years = yearsQuery.data ?? [];
  // Holdings stay on `data` so a period switch never flashes the empty state.
  const savings = savingsQuery.data?.savings ?? [];
  const savingsResponse = savingsQuery.currentData;
  const periodSavings = savingsResponse?.savings ?? [];
  const totalsStatus = periodStatusOf(savingsQuery);
  const chartStatus = periodStatusOf(historyQuery);
  const savingsTotals = useMemo(
    () => ({
      investedValue: savingsResponse?.investedValue ?? 0,
      currentValue: savingsResponse?.currentValue ?? 0,
      earnedValue: savingsResponse?.earnedValue ?? 0,
      withdrawnValue: savingsResponse?.withdrawnValue ?? 0,
      taxWithheld: savingsResponse?.taxWithheld ?? 0,
      grossInterest: savingsResponse?.grossInterest ?? null,
      cashbackValue: savingsResponse?.cashbackValue ?? 0,
    }),
    [savingsResponse],
  );
  const history = historyQuery.data ?? EMPTY_HISTORY;
  const periodHistory = historyQuery.currentData ?? EMPTY_HISTORY;
  const totalSavings = allSavingsQuery.data?.savings.length ?? 0;
  const isLoading = savingsQuery.isLoading || historyQuery.isLoading;

  const periodOptions = useMemo(
    () => [
      { label: "All time", value: ALL_TIME },
      ...years.map((year) => ({
        label: year.toString(),
        value: year.toString(),
      })),
    ],
    [years],
  );

  // Reset to All time when the selected year drops out of the available list
  // (e.g. after toggling Active only excludes its only saving).
  useEffect(() => {
    if (periodValue === ALL_TIME) {
      return;
    }
    if (!years.some((year) => year.toString() === periodValue)) {
      setPeriodValue(ALL_TIME);
    }
  }, [years, periodValue]);

  const handleCreateSaving = async (values: SavingFormValues) => {
    const payload: CreateSavingsPayload = {
      ...cashbackPayload(values),
      name: values.name,
      institution: values.institution === "" ? undefined : values.institution,
      startDate: formatDate(values.startDate),
    };
    try {
      await createSavings(payload).unwrap();
      notify.success("Saving created!");
      setIsOpen(false);
    } catch (err: any) {
      notify.apiError(err);
    }
  };

  const navigateTo = (id: number) => {
    setLocation("/savings/" + id);
  };

  const handleClearFilters = useCallback(() => {
    setPeriodValue(ALL_TIME);
    setActiveOnly(false);
  }, []);

  const growth = useMemo(
    () => buildSavingsGrowth(periodHistory),
    [periodHistory],
  );

  // The chip is gross interest only, like the Saving page chip; cashback has its own substat.
  const heroStats = useMemo(() => {
    const netContributed =
      savingsTotals.investedValue - savingsTotals.withdrawnValue;
    const changeValue = headlineInterestOf(savingsTotals);
    let changePercent: number | null = null;
    if (netContributed > 0) {
      changePercent = (changeValue / netContributed) * 100;
    }
    return { netContributed, changeValue, changePercent };
  }, [savingsTotals]);

  const periodLabel = periodValue === ALL_TIME ? "All time" : periodValue;

  // Empty state. Savings exist somewhere but none match the current filters ->
  // "no match". No savings at all -> genuine first-run onboarding.
  const isEmpty = savings.length === 0;
  const hasAnySavings = totalSavings > 0;
  const isFilteredEmpty = isEmpty && hasAnySavings;

  // Surface the FAB only where the Hero's "Create savings" button is hidden: the
  // populated view on compact layouts. The empty state keeps its own button.
  const showCreateFab = isCompact && !isEmpty;
  const filtersVariant = isCompact ? "standalone" : "inline";

  const filterProps = {
    isCompact,
    periodValue,
    periodOptions,
    onPeriodChange: setPeriodValue,
    activeOnly,
    onActiveOnlyChange: setActiveOnly,
    count: savings.length,
    total: Math.max(totalSavings, savings.length),
    sort,
    onSortChange: setSort,
  };

  // Only swap in the skeleton on the very first load. Filter-triggered refetches
  // keep the current content mounted so the page doesn't appear to fully reload.
  if (isLoading && !savingsQuery.data) {
    return <SavingsSkeleton />;
  }

  if (savingsQuery.isError && !savingsQuery.data) {
    return (
      <Wrapper>
        <ErrorState
          message="Unable to load savings. Please try again."
          onRetry={async () => {
            await Promise.all(
              [savingsQuery, historyQuery, allSavingsQuery].map((query) =>
                query.refetch(),
              ),
            );
          }}
        />
      </Wrapper>
    );
  }

  const populatedView = (
    <>
      <SavingsHero
        periodLabel={periodLabel}
        currentValue={savingsTotals.currentValue}
        depositedValue={savingsTotals.investedValue}
        withdrawnValue={savingsTotals.withdrawnValue}
        cashbackValue={savingsTotals.cashbackValue}
        netContributed={heroStats.netContributed}
        changeValue={heroStats.changeValue}
        changePercent={heroStats.changePercent}
        chartSeries={growth.series}
        chartXLabels={growth.xLabels}
        totalsStatus={totalsStatus}
        chartStatus={chartStatus}
        onRetryTotals={savingsQuery.refetch}
        onRetryChart={historyQuery.refetch}
        onCreateSavings={() => setIsOpen(true)}
      />

      <SavingsFilters
        variant={filtersVariant}
        {...filterProps}
        showSort={true}
      />

      <SavingsInsights
        savings={periodSavings}
        status={totalsStatus}
        onRetry={savingsQuery.refetch}
        isCompact={isCompact}
      />

      <SavingsHoldings
        savings={savings}
        bySaving={history.bySaving}
        onSelect={navigateTo}
        sort={sort}
        onSortChange={setSort}
      />
    </>
  );

  // Surface the filter controls in the "no match" state so the period and
  // "Active only" toggle the copy refers to are actually reachable.
  const emptyFilters = isFilteredEmpty ? (
    <SavingsFilters
      variant={filtersVariant}
      {...filterProps}
      showSort={false}
    />
  ) : undefined;

  const emptyView = (
    <>
      <EmptySavingsState
        isFiltered={isFilteredEmpty}
        onCreateSavings={() => setIsOpen(true)}
        onClearFilters={handleClearFilters}
      />
      {emptyFilters}
    </>
  );

  return (
    <Wrapper>
      {isEmpty ? emptyView : populatedView}

      {showCreateFab && (
        <CreateFab onClick={() => setIsOpen(true)} ariaLabel="Create savings" />
      )}

      {isOpen && (
        <SavingsPanel
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          handleOnSubmit={handleCreateSaving}
        />
      )}
    </Wrapper>
  );
};
